import { RefreshCw, Loader2 } from 'lucide-react';
import BrandMark from './BrandMark';
import { useBrand } from '../../hooks/useBrand';

interface OfflinePageProps {
  onRefresh?: () => void;
  isError?: boolean;
  isLoading?: boolean;
}

/**
 * Full-screen offline/error/loading page with app intro and refresh button.
 * Shown on first load (brief), when offline, or when API fails.
 */
export default function OfflinePage({ onRefresh, isError, isLoading }: OfflinePageProps) {
  const brand = useBrand();

  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-950 px-6"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'max(1.5rem, env(safe-area-inset-left))',
        paddingRight: 'max(1.5rem, env(safe-area-inset-right))',
      }}
    >
      {/* Logo & Brand */}
      <div className="flex flex-col items-center mb-8">
        <BrandMark variant="icon" size="h-20 w-20" className="mb-4" />
        <h1 className="text-2xl font-bold text-white tracking-tight">tropayment</h1>
        <p className="text-gray-400 text-sm mt-1">Crypto Payment Gateway</p>
      </div>

      {/* Message */}
      <div className="text-center max-w-sm mb-8">
        {isLoading ? (
          <>
            <p className="text-gray-300 text-base mb-2">Connecting...</p>
            <p className="text-gray-500 text-sm mb-4">
              {brand.fullName} lets you accept cryptocurrency payments instantly.
            </p>
            <Loader2 size={28} className="animate-spin text-blue-400 mx-auto" />
          </>
        ) : isError ? (
          <>
            <p className="text-gray-300 text-base mb-2">Connection failed</p>
            <p className="text-gray-500 text-sm">
              Unable to reach the server. Please check your internet connection and try again.
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-300 text-base mb-2">You're offline</p>
            <p className="text-gray-500 text-sm">
              {brand.fullName} lets you accept cryptocurrency payments instantly. Connect to the internet to continue.
            </p>
          </>
        )}
      </div>

      {/* Retry Button - disabled during retry */}
      <button
        onClick={handleRefresh}
        disabled={isLoading}
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition"
      >
        <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Connecting...' : 'Retry'}
      </button>
    </div>
  );
}
